import './App.css';
import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import useAutoLogout from './hooks/useAutoLogout';
import ScrollToTop from './ScrollToTop';
import RequireAuth from './RequireAuth';
import { DelayedPageLoader } from './components/common/PageLoader';
import SEO from './components/common/SEO';

// ── Layouts (petits, chargés directement) ─────────────────
import { MainLayout }  from './Layout/MainLayout';
import { AuthLayout }  from './Layout/AuthLayout';
import CompareBar      from './components/Compare/CompareBar';

// ── Page d'accueil (chargée directement — 1ère page visible) ─
import { Hero }          from './components/Hero/Hero';
// StatsBar (compteurs annonces/vendeurs/villes) volontairement pas affiché
// tant que le site est encore peu peuplé — des chiffres bas donneraient une
// mauvaise première impression. Composant + endpoint /api/stats/public
// conservés, à réactiver ici une fois qu'il y aura du volume réel.
import { BodyTypeSearch } from './components/BodyTypeSearch/BodyTypeSearch';
import { BrandsTypeSearch } from './components/BrandsTypeSearch/BrandsTypeSearch';
import PromoCars         from './components/PromoCars/PromoCars';
import LatestListings    from './components/LatestListings/LatestListings';
import { Services }      from './components/Services/Services';
import { NewCars }       from './components/NewCars/NewCars';
import RecentlyViewed    from './components/RecentlyViewed/RecentlyViewed';
import { Reviews }       from './components/Reviews/Reviews';
import { Footer }        from './components/Footer/Footer';

// ── Toutes les autres pages — lazy loaded ─────────────────
const Login           = lazy(() => import('./components/Login/Login'));
const Register        = lazy(() => import('./components/Login/Register'));
const ForgotPassword  = lazy(() => import('./components/Login/ForgotPassword'));
const ResetPassword   = lazy(() => import('./components/Login/ResetPassword'));
const ConfirmEmail    = lazy(() => import('./ConfirmEmail'));

const FeaturedCars    = lazy(() => import('./components/FeaturedCars/FeaturedCars').then(m => ({ default: m.FeaturedCars })));
const CarDetails      = lazy(() => import('./components/CarDetails/CarDetails'));
const SellerProfile   = lazy(() => import('./components/SellerProfile/SellerProfile'));
const ComparePage     = lazy(() => import('./components/Compare/ComparePage'));

const AddCars         = lazy(() => import('./components/AddCars/AddCars'));
const MultiStepCarForm = lazy(() => import('./components/AddCars/MultiStepCarForm'));
const MyCars          = lazy(() => import('./components/MyCars/MyCars'));
const Dashboard       = lazy(() => import('./components/MyCars/Dashboard'));
const Statistics      = lazy(() => import('./components/MyCars/Statistics'));
const Messages        = lazy(() => import('./components/MyCars/Messages'));
const Settings        = lazy(() => import('./components/MyCars/Settings'));
const ProfilePage     = lazy(() => import('./components/ProfilePage/ProfilePage'));
const Favorites       = lazy(() => import('./components/Favorites/Favorites'));
const Unauthorized    = lazy(() => import('./components/Unauthorized/Unauthorized'));

const Subscriptions      = lazy(() => import('./components/Subscriptions/Subscriptions'));

const AdminOverview      = lazy(() => import('./components/Admin/AdminOverview'));
const AdminVerifications = lazy(() => import('./components/Admin/AdminVerifications'));
const AdminUsers         = lazy(() => import('./components/Admin/AdminUsers'));
const AdminCars          = lazy(() => import('./components/Admin/AdminCars'));

function App() {
  useAutoLogout();

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <CompareBar />

        <Suspense fallback={<DelayedPageLoader />}>
          <Routes>

            {/* ── Page d'accueil ─────────────────────────── */}
            <Route path="/" element={
              <>
                <SEO
                  title="Voitures, motos et utilitaires d'occasion au Maroc"
                  description="Achetez ou vendez une voiture, une moto ou un véhicule utilitaire d'occasion au Maroc. Annonces vérifiées, mise en ligne gratuite en 5 minutes."
                  path="/"
                />
                <Hero />
                <BodyTypeSearch />
                <BrandsTypeSearch />
                <PromoCars />
                <LatestListings />
                <Services />
                <NewCars />
                <RecentlyViewed />
                <Reviews />
                <Footer />
              </>
            } />

            {/* ── Auth pages ─────────────────────────────── */}
            <Route element={<AuthLayout />}>
              <Route path="/reset-password"  element={<ResetPassword />} />
              <Route path="/ForgotPassword"  element={<ForgotPassword />} />
              <Route path="/confirm-email"   element={<ConfirmEmail />} />
            </Route>

            {/* ── Public pages ───────────────────────────── */}
            <Route element={<MainLayout />}>
              <Route path="/Login"           element={<Login />} />
              <Route path="/Register"        element={<Register />} />
              <Route path="/featured-cars"   element={<FeaturedCars />} />
              <Route path="/cars-details/:id" element={<CarDetails />} />
              <Route path="/motos-details/:id" element={<CarDetails />} />
              <Route path="/utilitaires-details/:id" element={<CarDetails />} />
              <Route path="/profile/:id"     element={<SellerProfile />} />
              <Route path="/comparer"        element={<ComparePage />} />
              <Route path="/unauthorized"    element={<Unauthorized />} />
            </Route>

            {/* ── Protected pages ────────────────────────── */}
            <Route element={
              <RequireAuth allowedRoles={['Admin', 'Professionnel', 'Particulier']}>
                <MainLayout />
              </RequireAuth>
            }>
              <Route path="/AddCars"         element={<AddCars />} />
              <Route path="/add-car-details" element={<MultiStepCarForm />} />
              <Route path="/add-moto-details" element={<MultiStepCarForm />} />
              <Route path="/add-utilitaire-details" element={<MultiStepCarForm />} />
              <Route path="/edit-car"        element={<MultiStepCarForm />} />
              <Route path="/edit-moto"       element={<MultiStepCarForm />} />
              <Route path="/edit-utilitaire" element={<MultiStepCarForm />} />
              <Route path="/my-cars"         element={<MyCars />} />
              <Route path="/Statistics"      element={<Statistics />} />
              <Route path="/Messages"        element={<Messages />} />
              <Route path="/messages"        element={<Messages />} />
              <Route path="/Settings"        element={<Settings />} />
              <Route path="/Dashboard"       element={<Dashboard />} />
              <Route path="/profilePage"     element={<ProfilePage />} />
              <Route path="/favorites"       element={<Favorites />} />
            </Route>

            {/* ── Pages Professionnel uniquement ────────────── */}
            <Route element={
              <RequireAuth allowedRoles={['Professionnel']}>
                <MainLayout />
              </RequireAuth>
            }>
              <Route path="/subscriptions" element={<Subscriptions />} />
            </Route>

            {/* ── Admin pages ─────────────────────────────── */}
            <Route element={
              <RequireAuth allowedRoles={['Admin']}>
                <MainLayout />
              </RequireAuth>
            }>
              <Route path="/admin/overview"      element={<AdminOverview />} />
              <Route path="/admin/verifications" element={<AdminVerifications />} />
              <Route path="/admin/users"         element={<AdminUsers />} />
              <Route path="/admin/cars"          element={<AdminCars />} />
            </Route>

          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
